import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { Link2, Loader2, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Lettrage() {
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const fetchAll = async () => {
    try {
      const [sugRes, stRes] = await Promise.all([
        axios.get(`${API_URL}/lettrage/suggestions`),
        axios.get(`${API_URL}/lettrage/stats`),
      ]);
      setSuggestions(sugRes.data); setStats(stRes.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const toggleSelect = (txId) => setSelected(s => s.includes(txId) ? s.filter(x => x !== txId) : [...s, txId]);
  const selectAll = () => setSelected(suggestions.map(s => s.transaction_id));
  const selectNone = () => setSelected([]);

  const confirmSelected = async () => {
    if (selected.length === 0) return;
    setConfirming(true);
    try {
      const matches = selected.map(txId => {
        const sug = suggestions.find(s => s.transaction_id === txId);
        return { transaction_id: txId, invoice_id: sug?.invoice_id };
      }).filter(m => m.invoice_id);

      const res = await axios.post(`${API_URL}/lettrage/confirm`, { matches });
      toast.success(res.data.message);
      setSelected([]);
      fetchAll();
    } catch (e) { toast.error('Erreur'); }
    finally { setConfirming(false); }
  };

  const scoreColor = (score) => {
    if (score >= 0.9) return 'bg-green-100 text-green-700';
    if (score >= 0.7) return 'bg-blue-100 text-blue-700';
    return 'bg-amber-100 text-amber-700';
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold"><Link2 className="w-6 h-6 inline mr-2" />Lettrage automatique</h1>
          <p className="text-slate-500">Rapprochez automatiquement transactions et factures</p>
        </div>
        {selected.length > 0 && (
          <Button onClick={confirmSelected} disabled={confirming} className="bg-[#0F5156] hover:bg-[#0d464a]">
            {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Confirmer {selected.length} lettrage(s)
          </Button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-sm text-slate-500">Total transactions</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-green-600">{stats.matched}</p><p className="text-sm text-slate-500">Lettrées</p></CardContent></Card>
          <Card><CardContent className="pt-6 text-center"><p className="text-2xl font-bold text-amber-600">{stats.unmatched}</p><p className="text-sm text-slate-500">Non lettrées</p></CardContent></Card>
          <Card><CardContent className="pt-6"><Progress value={stats.match_rate} className="mb-2" /><p className="text-center text-sm">{stats.match_rate}%</p></CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle><Sparkles className="w-5 h-5 inline mr-2" />Suggestions de lettrage</CardTitle>
              <CardDescription>{suggestions.length} correspondance(s) trouvée(s)</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll}>Tout sélectionner</Button>
              <Button variant="outline" size="sm" onClick={selectNone}>Désélectionner</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Transaction</TableHead>
              <TableHead className="w-12 text-center"><ArrowRight className="w-4 h-4 mx-auto" /></TableHead>
              <TableHead>Facture</TableHead>
              <TableHead className="w-24">Score</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {suggestions.map(s => (
                <TableRow key={s.transaction_id} className={selected.includes(s.transaction_id) ? 'bg-green-50 dark:bg-green-900/10' : ''}>
                  <TableCell><Checkbox checked={selected.includes(s.transaction_id)} onCheckedChange={() => toggleSelect(s.transaction_id)} /></TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{s.transaction_libelle}</p>
                    <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                      <span>{s.transaction_date}</span>
                      <span className={`font-mono font-medium ${s.transaction_montant >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(s.transaction_montant)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center"><ArrowRight className="w-4 h-4 text-slate-300 mx-auto" /></TableCell>
                  <TableCell>
                    <p className="text-sm font-medium">{s.invoice_ref}</p>
                    <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                      <span>{s.invoice_supplier}</span>
                      <span className="font-mono">{formatCurrency(s.invoice_amount)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={`${scoreColor(s.score)} text-xs`}>
                      {Math.round(s.score * 100)}%
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              {suggestions.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-green-300" />
                  <p>Aucune suggestion — toutes les transactions sont lettrées ou aucune correspondance trouvée.</p>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
